import styled, { css, keyframes } from 'styled-components';

import { colors, ds } from '@/config/colors';

import { media } from '../../utils/mediaQueries';

/* ==========================================================================
   Wejście ekranu — jeden ruch dla wszystkich bloków.

   Elementy pojawiają się po kolei, od nagłówka po stopkę: oko dostaje kolejność
   czytania, a ekran nie „wskakuje" całością. Animacja jest krótka i przesuwa
   o kilkanaście pikseli, więc nie opóźnia pracy nikomu, kto zna ten formularz na pamięć.
   ========================================================================== */
const authRise = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

/** Wejście z opóźnieniem. Bez `both` element mignąłby w pełnej widoczności przed startem. */
export const rise = (delay: number) => css`
  animation: ${authRise} 420ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms both;
`;

/* ==========================================================================
   Akcent auth: accent-ink z design systemu (główne przyciski, zaznaczenia).
   W trybie ciemnym akcent jest odwrócony, bo grafit zlałby się z ciemnym tłem.
   ========================================================================== */
export const authAccentBg = (mode: string) => (mode === 'dark' ? '#f9fafb' : ds.accentInk);
export const authAccentBgHover = (mode: string) => (mode === 'dark' ? '#ffffff' : ds.accentInkHover);
export const authAccentOn = (mode: string) => (mode === 'dark' ? ds.accentInk : ds.bgSurface);
export const authAccentText = (mode: string) => (mode === 'dark' ? '#e5e7eb' : ds.textPrimary);
export const authAccentBorder = (mode: string) => (mode === 'dark' ? '#9ca3af' : ds.accentInk);
export const authAccentRing = (mode: string) => (mode === 'dark'
  ? 'rgba(255, 255, 255, 0.15)'
  : 'rgba(17, 24, 39, 0.15)');

export const WelcomeTitle = styled.h1`
  font-size: 24px;
  font-weight: 500;
  line-height: 1.3;
  letter-spacing: -0.015em;
  text-wrap: balance;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#ffffff' : ds.textPrimary)};
  margin: 0 0 8px 0;
`;

export const WelcomeSubtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#a1a1a1' : ds.textSecondary)};
  margin: 0 0 28px 0;
  line-height: 1.6;
  text-wrap: pretty;
`;

export const AuthForm = styled.form`
  width: 100%;

  /* Kontynuacja rytmu z kolumny formularza: najpierw nagłówek, potem pola, na końcu przycisk. */
  > *:nth-child(1) { ${rise(260)} }
  > *:nth-child(2) { ${rise(320)} }
  > *:nth-child(3) { ${rise(380)} }
  > *:nth-child(n + 4) { ${rise(430)} }

  ${media.reducedMotion} {
    > * {
      animation: none;
    }
  }
`;

export const InputGroup = styled.div`
  margin-bottom: 20px;
`;

export const InputLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#e5e5e5' : ds.textPrimary)};
  margin-bottom: 6px;
`;

/* Komunikat walidacji pod polem: caption, czerwień o kontraście AA na bieli. */
export const FieldError = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: ${colors.danger[700]};
`;

export const InputWrapper = styled.div`
  position: relative;

  /* Ikona ciemnieje razem z ramką, więc aktywne pole widać jednym rzutem oka. */
  &:focus-within > div:first-child {
    color: ${({ theme }) => authAccentBorder(theme.mode)};
  }
`;

export const InputIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => (theme.mode === 'dark' ? '#6b7280' : ds.textMuted)};
  display: flex;
  align-items: center;
  pointer-events: none;
  transition: color 0.18s ease;

  svg {
    width: 18px;
    height: 18px;
    stroke-width: 1.75;
  }
`;

/* Pole z DS: r/8, 1px border-default, hover border-strong, focus accent-ink.
   16 px na telefonie, żeby iOS nie powiększał strony przy fokusie. */
export const StyledInput = styled.input`
  width: 100%;
  height: 48px;
  padding: 0 44px 0 42px;
  font-size: 14px;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : ds.borderDefault)};
  border-radius: 8px;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#111111' : ds.bgSurface)};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#ffffff' : ds.textPrimary)};
  caret-color: ${({ theme }) => authAccentBorder(theme.mode)};
  transition: border-color 0.18s ease, box-shadow 0.18s ease;

  ${media.mobile} {
    font-size: 16px;
  }

  &:hover:not(:focus) {
    border-color: ${({ theme }) => (theme.mode === 'dark' ? '#4b5563' : ds.borderStrong)};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => authAccentBorder(theme.mode)};
    box-shadow: 0 0 0 3px ${({ theme }) => authAccentRing(theme.mode)};
  }

  &[aria-invalid='true'] {
    border-color: ${colors.danger[600]};
  }

  &::placeholder {
    color: ${({ theme }) => (theme.mode === 'dark' ? '#6b7280' : ds.textMuted)};
  }

  /* Podpowiedź menedżera haseł ma tło przeglądarki; wyrównujemy je do pola. */
  &:-webkit-autofill,
  &:-webkit-autofill:focus {
    -webkit-text-fill-color: ${({ theme }) => (theme.mode === 'dark' ? '#ffffff' : ds.textPrimary)};
    box-shadow: 0 0 0 100px ${({ theme }) => (theme.mode === 'dark' ? '#111111' : ds.bgSurface)} inset;
  }
`;

export const PasswordToggle = styled.button`
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#6b7280' : ds.textMuted)};
  cursor: pointer;
  width: 40px;
  height: 40px;
  justify-content: center;
  border-radius: 9999px;
  display: flex;
  align-items: center;

  &:hover {
    color: ${({ theme }) => (theme.mode === 'dark' ? '#e5e5e5' : ds.textPrimary)};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => authAccentRing(theme.mode)};
  }

  svg {
    width: 18px;
    height: 18px;
    stroke-width: 1.75;
  }
`;

export const RememberRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-bottom: 24px;
`;

export const CheckboxWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  min-height: 44px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#a1a1a1' : ds.textSecondary)};

  input[type="checkbox"] {
    appearance: none;
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    min-width: 16px;
    border-radius: 4px;
    border: 1.5px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : ds.borderStrong)};
    cursor: pointer;
    position: relative;
    transition: all 0.15s ease;
    background: transparent;

    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px ${({ theme }) => authAccentRing(theme.mode)};
    }

    &:checked {
      background: ${({ theme }) => authAccentBg(theme.mode)};
      border-color: ${({ theme }) => authAccentBorder(theme.mode)};

      &::after {
        content: '';
        position: absolute;
        left: 4px;
        top: 1px;
        width: 5px;
        height: 9px;
        border: solid ${({ theme }) => authAccentOn(theme.mode)};
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
      }
    }
  }
`;

export const ForgotPasswordLink = styled.a`
  font-size: 14px;
  color: ${({ theme }) => authAccentText(theme.mode)};
  text-decoration: none;
  text-underline-offset: 4px;
  font-weight: 500;
  border-radius: 4px;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => authAccentRing(theme.mode)};
  }
`;

/* Główny przycisk z DS: rounded-full, accent-ink, biały tekst, min. 44 px wysokości,
   hover przez lekkie rozjaśnienie (jak hover:opacity-90 w hotelspot-audit). */
export const SubmitButton = styled.button`
  width: 100%;
  height: 48px;
  padding: 0 24px;
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => authAccentOn(theme.mode)};
  background: ${({ theme }) => authAccentBg(theme.mode)};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s ease, opacity 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: ${({ theme }) => authAccentBgHover(theme.mode)};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => authAccentBorder(theme.mode)};
    outline-offset: 3px;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.7;
  }
`;

export const LegalDisclaimer = styled.p`
  text-align: center;
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#8a8a8a' : ds.textSecondary)};
  margin-top: 20px;
  max-width: 360px;
  margin-left: auto;
  margin-right: auto;
  text-wrap: pretty;

  a {
    color: ${({ theme }) => (theme.mode === 'dark' ? '#a1a1a1' : ds.textPrimary)};
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover {
      color: ${({ theme }) => authAccentText(theme.mode)};
    }
  }
`;

export const SignUpLink = styled.p`
  text-align: center;
  font-size: 14px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#a1a1a1' : ds.textSecondary)};
  margin-top: 24px;

  a {
    color: ${({ theme }) => authAccentText(theme.mode)};
    text-decoration: none;
    text-underline-offset: 4px;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

// ============================================
// Footer Components
// ============================================

export const AuthFooter = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px 24px;
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(26, 26, 26, 0.95)' : 'rgba(255, 255, 255, 0.95)')};
  backdrop-filter: blur(10px);
  border-top: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#dcdfe4')};
  z-index: 100;
`;

export const LanguageLabel = styled.span`
  font-size: 13px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  font-weight: 500;
`;

export const LanguageSelect = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  font-size: 13px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#e5e5e5' : '#374151')};
  background: transparent;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#d1d5db')};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => (theme.mode === 'dark' ? '#262626' : '#f9fafb')};
    border-color: ${({ theme }) => (theme.mode === 'dark' ? '#4b5563' : '#9ca3af')};
  }

  svg {
    opacity: 0.7;
  }
`;

/* Starsze ekrany (InvitePin, SetPassword, UserInvite) dostają te same style co logowanie. */
export const Title = styled(WelcomeTitle)``;

export const Content = styled(WelcomeSubtitle)``;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

export const PrimaryButton = styled(SubmitButton)<{ isLoading?: boolean }>``;

export const BackLink = styled.div`
  text-align: center;
  margin-top: 24px;

  a {
    text-underline-offset: 4px;
    font-size: 14px;
    color: ${({ theme }) => authAccentText(theme.mode)};
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;
